import React, { useState, useEffect } from 'react';
import { IProject } from '../../types/Project';
import styles from './ProjectForm.module.css';
import { useNavigate } from 'react-router-dom';
import { uploadImage } from '../../services/UploadService';
import { IProjectImage } from '../../types/ProjectImage';
import { 
  StarIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { MapLocationPicker } from './MapLocationPicker';

interface ProjectFormProps {
    initialData?: IProject;
    onSubmit: (data: IProject, imagesToDelete: string[]) => void;
    isEditing: boolean;
    sessionUploadsRef?: React.MutableRefObject<string[]>;
}

const emptyProject: IProject = { 
    id: Date.now(), 
    title: '', 
    description: '', 
    isFeatured: false, 
    isHidden: false, 
    category: 'Habitacional',
    year: new Date().getFullYear().toString(), 
    state: '', 
    city: '', 
    ubication: '',
    superficie: '', 
    fases: 'Proyecto', 
    imageUrl: '', 
    gallery: [],
    showMap: false
};

const categories = ['Habitacional', 'Comercios y servicios', 'Usos mixtos','Espacio público', 'Educación y cultural'];
const phases = ['Proyecto', 'En construcción', 'En obra', 'Finalizado', 'Licitación', 'Prototipo', 'Estudio'];

const ProjectForm: React.FC<ProjectFormProps> = ({ initialData, onSubmit, isEditing, sessionUploadsRef }) => {
    const [formData, setFormData] = useState<IProject>(emptyProject);
    const [isUploading, setIsUploading] = useState(false);
    const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'basic' | 'location' | 'gallery'>('basic');
    const navigate = useNavigate();

    useEffect(() => {
        if (initialData) {
            const sanitizedData = { ...emptyProject, ...initialData };
            setFormData(sanitizedData);
        } else {
            setFormData(emptyProject);
        }
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUbicationChange = (ubication: string) => {
        setFormData(prev => ({ ...prev, ubication }));
    };

    const handleShowMapChange = (showMap: boolean) => {
        setFormData(prev => ({ ...prev, showMap }));
    };

    const handleGalleryFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !formData.id) return;
        setIsUploading(true);
        for (const file of Array.from(files)) {
            try {
                const response = await uploadImage(file, 'projects', formData.id);
                const newImage: IProjectImage = {
                  id: 0,
                  url: response.url,
                  caption: '',
                  description: ''
                };
                setFormData(prev => ({ ...prev, gallery: [...prev.gallery, newImage] }));
                sessionUploadsRef?.current.push(response.url);
            } catch (error) { 
                alert(`Falló la subida de ${file.name}.`); 
            }
        }
        setIsUploading(false);
    };
    
    const handleRemoveFromGallery = (urlToRemove: string) => {
        setImagesToDelete(prev => [...prev, urlToRemove]);
        setFormData(prev => ({ ...prev, gallery: prev.gallery.filter(image => image.url !== urlToRemove) }));
        
        if (formData.imageUrl === urlToRemove) {
            setFormData(prev => ({ ...prev, imageUrl: '' }));
        }
    };

    const handleGalleryInputChange = (
        index: number, 
        field: 'caption' | 'description', 
        value: string
    ) => {
        setFormData(prev => {
            const newGallery = prev.gallery.map((image, i) => 
                i === index ? { ...image, [field]: value } : image
            );
            return { ...prev, gallery: newGallery };
        });
    };

    const handleSetMainImage = (url: string) => {
        setFormData(prev => ({ ...prev, imageUrl: url }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData, imagesToDelete);
    };

    return (
        <div className={styles.formContainer}>
            {/* Header con pestañas */}
            <div className={styles.formHeader}>
                <h2 className={styles.formTitle}>
                    {isEditing ? 'Editar Proyecto' : 'Crear Nuevo Proyecto'}
                </h2>
                <div className={styles.tabNavigation}>
                    <button
                        type="button"
                        className={`${styles.tabButton} ${activeTab === 'basic' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('basic')}
                    >
                        <InformationCircleIcon className={styles.tabIcon} />
                        Información Básica
                    </button>
                    <button
                        type="button"
                        className={`${styles.tabButton} ${activeTab === 'location' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('location')}
                    >
                        <MapPinIcon className={styles.tabIcon} />
                        Ubicación
                    </button>
                    {isEditing && (
                        <button
                            type="button"
                            className={`${styles.tabButton} ${activeTab === 'gallery' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('gallery')}
                        >
                            <PhotoIcon className={styles.tabIcon} />
                            Galería ({formData.gallery.length})
                        </button>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Pestaña: Información Básica */}
                {activeTab === 'basic' && (
                    <div className={styles.tabContent}>
                        <div className={styles.formGrid}>
                            {/* Columna 1 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="title" className={styles.formLabel}>
                                        Título del Proyecto *
                                    </label>
                                    <input 
                                        type="text" 
                                        id="title" 
                                        name="title" 
                                        value={formData.title} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: Casa Moderna en Bosque"
                                        required 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="category" className={styles.formLabel}>
                                        Categoría *
                                    </label>
                                    <select 
                                        id="category" 
                                        name="category" 
                                        value={formData.category} 
                                        onChange={handleChange} 
                                        className={styles.formSelect}
                                        required
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="fases" className={styles.formLabel}>
                                        Fase del Proyecto *
                                    </label>
                                    <select 
                                        id="fases" 
                                        name="fases" 
                                        value={formData.fases} 
                                        onChange={handleChange} 
                                        className={styles.formSelect}
                                        required
                                    >
                                        {phases.map(phase => (
                                            <option key={phase} value={phase}>{phase}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="year" className={styles.formLabel}>
                                        Año del Proyecto
                                    </label>
                                    <input 
                                        type="text" 
                                        id="year" 
                                        name="year" 
                                        value={formData.year} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: 2024"
                                    />
                                </div>
                            </div>

                            {/* Columna 2 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="state" className={styles.formLabel}>
                                        Estado
                                    </label>
                                    <input 
                                        type="text" 
                                        id="state" 
                                        name="state" 
                                        value={formData.state} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: Ciudad de México"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="city" className={styles.formLabel}>
                                        Ciudad
                                    </label>
                                    <input 
                                        type="text" 
                                        id="city" 
                                        name="city" 
                                        value={formData.city} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: Polanco"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="superficie" className={styles.formLabel}>
                                        Superficie (m²)
                                    </label>
                                    <input 
                                        type="text" 
                                        id="superficie" 
                                        name="superficie" 
                                        value={formData.superficie} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: 150"
                                    />
                                </div>

                                <div className={styles.optionsGroup}>
                                    <label className={styles.formLabel}>Opciones del Proyecto</label>
                                    <div className={styles.checkboxCard}>
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isFeatured" 
                                                name="isFeatured" 
                                                checked={formData.isFeatured} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            <StarIcon className={styles.checkboxIcon} />
                                            <span className={styles.checkboxText}>
                                                <strong>Proyecto Destacado</strong>
                                                <small>Aparecerá en la página principal</small>
                                            </span>
                                        </label>
                                        
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isHidden" 
                                                name="isHidden" 
                                                checked={formData.isHidden} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            {formData.isHidden ? (
                                                <EyeSlashIcon className={styles.checkboxIcon} />
                                            ) : (
                                                <EyeIcon className={styles.checkboxIcon} />
                                            )}
                                            <span className={styles.checkboxText}>
                                                <strong>Proyecto Oculto</strong>
                                                <small>No visible al público</small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Descripción - Full Width */}
                        <div className={styles.formGroupFull}>
                            <label htmlFor="description" className={styles.formLabel}>
                                Descripción del Proyecto *
                                <span className={styles.charCount}>
                                    {formData.description.length}/2000 caracteres
                                </span>
                            </label>
                            <textarea 
                                id="description" 
                                name="description" 
                                value={formData.description} 
                                onChange={handleChange} 
                                className={styles.formTextarea}
                                placeholder="Describe el proyecto en detalle: concepto, características principales, materiales utilizados, proceso de diseño..."
                                rows={8}
                                maxLength={2000}
                                required 
                            />
                            <div className={styles.textareaFooter}>
                                <span className={styles.helperText}>
                                    Esta descripción aparecerá en la página principal del proyecto
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pestaña: Ubicación */}
                {activeTab === 'location' && (
                    <div className={styles.tabContent}>
                        <div className={styles.locationSection}>
                            <div className={styles.sectionHeader}>
                                <MapPinIcon className={styles.sectionIcon} />
                                <div>
                                    <h3 className={styles.sectionTitle}>Ubicación del Proyecto</h3>
                                    <p className={styles.sectionDescription}>
                                        Selecciona la ubicación en el mapa o ingresa la dirección manualmente
                                    </p>
                                </div>
                            </div>
                            
                            <MapLocationPicker
                                initialUbication={formData.ubication}
                                onUbicationChange={handleUbicationChange}
                                showMap={formData.showMap}
                                onShowMapChange={handleShowMapChange}
                            />
                        </div>
                    </div>
                )}

                {/* Pestaña: Galería */}
                {activeTab === 'gallery' && (
                    <div className={styles.tabContent}>
                        <div className={styles.gallerySection}>
                            <div className={styles.sectionHeader}>
                                <PhotoIcon className={styles.sectionIcon} />
                                <div>
                                    <h3 className={styles.sectionTitle}>Galería de Imágenes</h3>
                                    <p className={styles.sectionDescription}>
                                        Gestiona las imágenes de tu proyecto. Puedes añadir títulos y descripciones detalladas.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.uploadSection}>
                                <label htmlFor="gallery" className={styles.uploadLabel}>
                                    <PlusIcon className={styles.uploadIcon} />
                                    <span>Agregar Imágenes</span>
                                    <input 
                                        type="file" 
                                        id="gallery" 
                                        name="gallery" 
                                        onChange={handleGalleryFilesChange} 
                                        multiple 
                                        disabled={isUploading}
                                        accept="image/*"
                                        className={styles.uploadInput}
                                    />
                                </label>
                                {isUploading && (
                                    <div className={styles.uploadingIndicator}>
                                        <div className={styles.spinner}></div>
                                        Subiendo imágenes...
                                    </div>
                                )}
                            </div>

                            {formData.gallery.length > 0 ? (
                                <div className={styles.galleryGrid}>
                                    {formData.gallery.map((image, index) => {
                                        const isMainImage = formData.imageUrl === image.url;
                                        return (
                                            <div key={image.id || image.url} className={`${styles.galleryCard} ${isMainImage ? styles.isMain : ''}`}>
                                                <div className={styles.galleryImageContainer}>
                                                    <img src={image.url} alt={`Imagen ${index + 1}`} className={styles.galleryImage} />
                                                    
                                                    <div className={styles.imageOverlay}>
                                                        <button 
                                                            type="button" 
                                                            className={styles.imageActionButton}
                                                            onClick={() => window.open(image.url, '_blank')}
                                                            title="Ver imagen completa"
                                                        >
                                                            <EyeIcon className={styles.actionIcon} />
                                                        </button>
                                                        
                                                        {!isMainImage && (
                                                            <button 
                                                                type="button" 
                                                                className={styles.imageActionButton}
                                                                onClick={() => handleSetMainImage(image.url)}
                                                                title="Establecer como imagen principal"
                                                            >
                                                                <StarIcon className={styles.actionIcon} />
                                                            </button>
                                                        )}
                                                        
                                                        <button 
                                                            type="button" 
                                                            className={`${styles.imageActionButton} ${styles.deleteButton}`}
                                                            onClick={() => handleRemoveFromGallery(image.url)}
                                                            title="Eliminar imagen"
                                                        >
                                                            <TrashIcon className={styles.actionIcon} />
                                                        </button>
                                                    </div>

                                                    {isMainImage && (
                                                        <div className={styles.mainImageBadge}>
                                                            <StarIcon className={styles.badgeIcon} />
                                                            Principal
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.imageInfo}>
                                                    <div className={styles.inputGroup}>
                                                        <label className={styles.inputLabel}>
                                                            Título de la imagen
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={image.caption || ''}
                                                            onChange={(e) => handleGalleryInputChange(index, 'caption', e.target.value)}
                                                            className={styles.infoInput}
                                                            placeholder="Ej: Fachada principal, Comedor, Dormitorio..."
                                                        />
                                                    </div>

                                                    <div className={styles.inputGroup}>
                                                        <label className={styles.inputLabel}>
                                                            Descripción detallada
                                                            <span className={styles.charCount}>
                                                                {image.description?.length || 0}/500 caracteres
                                                            </span>
                                                        </label>
                                                        <textarea
                                                            value={image.description || ''}
                                                            onChange={(e) => handleGalleryInputChange(index, 'description', e.target.value)}
                                                            className={styles.infoTextarea}
                                                            placeholder="Describe esta imagen en detalle: materiales utilizados, proceso constructivo, características especiales..."
                                                            rows={4}
                                                            maxLength={500}
                                                        />
                                                        <div className={styles.helperText}>
                                                            Esta descripción aparecerá en la sección de artículo del proyecto
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={styles.emptyGallery}>
                                    <PhotoIcon className={styles.emptyIcon} />
                                    <h4>No hay imágenes en la galería</h4>
                                    <p>Agrega imágenes para mostrar en tu proyecto</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Botones de acción */}
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={styles.cancelButton}
                        onClick={() => navigate('/projects')}
                    >
                        Cancelar
                    </button>
                    
                    <div className={styles.actionButtons}>
                        {activeTab !== 'basic' && (
                            <button 
                                type="button" 
                                className={styles.secondaryButton}
                                onClick={() => setActiveTab(activeTab === 'location' ? 'basic' : 'location')}
                            >
                                {activeTab === 'location' ? '← Información Básica' : 'Siguiente →'}
                            </button>
                        )}
                        
                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <div className={styles.spinner}></div>
                                    Procesando...
                                </>
                            ) : (
                                isEditing ? 'Guardar Cambios' : 'Crear Proyecto'
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProjectForm;